import { useCallback, useMemo, useState } from "react";

import { useToast } from "@hightouch/htui";

import {
  useCreateFolderMutation,
  useMoveCampaignArtifactsToFolderMutation,
  useSavedMessageFoldersQuery,
  useSavedMessagesIndexQuery,
} from "src/graphql";
import { captureException } from "src/lib/monitoring";

import {
  buildFolderTree,
  flattenFolderTree,
  LCM_MESSAGES_FOLDER_TYPE,
  type MessageFolderNode,
  NO_FOLDER_VALUE,
} from "./saved-messages-utils";
import {
  buildVisibilityScopedFilter,
  useCanSeePrivateMessages,
} from "./use-private-message-visibility";

export type UseCreateMessageFolderResult = {
  /** Creates a folder (optionally under `parentId`); resolves to its id, or null on empty name / failure. */
  createFolder: (
    name: string,
    parentId?: string | null,
  ) => Promise<string | null>;
  isCreatingFolder: boolean;
};

/**
 * Shared folder-creation primitive for the saved-messages surfaces: trims, tags
 * the lifecycle-messages type, and toasts on failure.
 */
export function useCreateMessageFolder(): UseCreateMessageFolderResult {
  const { toast } = useToast();
  const { mutateAsync, isLoading } = useCreateFolderMutation();

  const createFolder = useCallback(
    async (
      name: string,
      parentId: string | null = null,
    ): Promise<string | null> => {
      const trimmed = name.trim();
      if (!trimmed) return null;
      try {
        const res = await mutateAsync({
          name: trimmed,
          type: LCM_MESSAGES_FOLDER_TYPE,
          parent_id: parentId,
        });
        return res.insert_folders_one?.id ?? null;
      } catch (error) {
        toast({
          id: "create-folder",
          title: "Failed to create folder",
          message: error instanceof Error ? error.message : undefined,
          variant: "error",
        });
        captureException(error);
        return null;
      }
    },
    [mutateAsync, toast],
  );

  return { createFolder, isCreatingFolder: isLoading };
}

/**
 * Direct per-folder message counts, keyed by folder id (own `folder_id`, not
 * subtree rollups). Reuses the SavedMessagesIndex projection already fetched by
 * the Messages tab (shared via react-query cache) — no extra aggregate query.
 */
export function useFolderMessageCounts(): Map<string, number> {
  const canSeePrivateMessages = useCanSeePrivateMessages();
  const { data } = useSavedMessagesIndexQuery(
    // Matches the Messages tab's empty-search filter shape so the two share a
    // react-query cache entry (see buildVisibilityScopedFilter).
    { filters: buildVisibilityScopedFilter({}, canSeePrivateMessages) },
    { keepPreviousData: true },
  );

  return useMemo(() => {
    const counts = new Map<string, number>();
    for (const row of data?.saved_messages ?? []) {
      if (row.folder_id == null) continue;
      counts.set(row.folder_id, (counts.get(row.folder_id) ?? 0) + 1);
    }
    return counts;
  }, [data]);
}

export function useFolderPickerState() {
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState<string | undefined>(undefined);
  const [isCreating, setIsCreating] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [newFolderParentId, setNewFolderParentId] = useState<string | null>(
    null,
  );

  const reset = () => {
    setSearch("");
    setSelectedId(undefined);
    setIsCreating(false);
    setNewFolderName("");
    setNewFolderParentId(null);
  };

  // Files into the selected folder (or the unfiled bucket when NO_FOLDER is
  // picked). Null while nothing is selected.
  const targetFolderId: string | null | undefined =
    selectedId == null
      ? undefined
      : selectedId === NO_FOLDER_VALUE
        ? null
        : selectedId;

  return {
    search,
    setSearch,
    selectedId,
    setSelectedId,
    isCreating,
    setIsCreating,
    newFolderName,
    setNewFolderName,
    newFolderParentId,
    setNewFolderParentId,
    targetFolderId,
    hasSelection: selectedId != null,
    reset,
  };
}

export type FolderPickerState = ReturnType<typeof useFolderPickerState>;

export type UseFolderFilingResult = {
  folders: MessageFolderNode[];
  counts: Map<string, number>;
  /**
   * Creates a folder (optionally nested under `parentId`) and resolves to its
   * id (null on failure).
   */
  createFolder: (
    name: string,
    parentId?: string | null,
  ) => Promise<string | null>;
  /** Files the messages into `folderId` (null unfiles them). */
  file: (artifactIds: string[], folderId: string | null) => Promise<boolean>;
  isCreatingFolder: boolean;
  isFiling: boolean;
};

/**
 * Data + mutations for the folder-filing popover. Creating a folder and filing
 * a message are two discrete steps: create returns the new folder so the caller
 * can select it, then filing runs on the primary action.
 */
export function useFolderFiling(): UseFolderFilingResult {
  const { toast } = useToast();

  const { data: foldersData } = useSavedMessageFoldersQuery({
    type: LCM_MESSAGES_FOLDER_TYPE,
  });
  const folders = useMemo(
    () => flattenFolderTree(buildFolderTree(foldersData?.folders ?? [])),
    [foldersData],
  );
  const counts = useFolderMessageCounts();

  const { createFolder, isCreatingFolder } = useCreateMessageFolder();
  const { mutateAsync: moveToFolder, isLoading: isFiling } =
    useMoveCampaignArtifactsToFolderMutation();

  const file = useCallback(
    async (
      artifactIds: string[],
      folderId: string | null,
    ): Promise<boolean> => {
      if (!artifactIds.length) return false;
      try {
        await moveToFolder({ artifactIds, folderId });
        toast({
          id: "file-to-folder",
          title: folderId ? "Added to folder" : "Removed from folder",
          variant: "success",
        });
        return true;
      } catch (error) {
        toast({
          id: "file-to-folder",
          title: "Failed to add to folder",
          message: error instanceof Error ? error.message : undefined,
          variant: "error",
        });
        captureException(error);
        return false;
      }
    },
    [moveToFolder, toast],
  );

  return { folders, counts, createFolder, file, isCreatingFolder, isFiling };
}
